import Joi from 'joi';
import tlds from '@sideway/address/lib/tlds';
import axios from 'axios';
import constants, { NO, PENDING, YES } from '../constants/constants';
import epcUrls from '../constants/urlConstants';
import {
  PRO_TYPE,
  PROMOTIONS_TYPE,
  RETARGETING_TYPE,
  SURVEYS_TYPE,
  typeToTitleMap
} from '../components/tableData';

let recaptchaAction = 'CALL_DGC';
let recaptchaTokenGenerator = null;
let sourceId = null;

const defaultHeaders = {
  'x-client-id': 'epc-web',
};

export const setRecaptchaTokenGenerator = (newRecaptchaTokenGenerator) => {
  recaptchaTokenGenerator = newRecaptchaTokenGenerator.executeRecaptcha || recaptchaTokenGenerator;
};

export const setSourceId = (channel) => {
  switch (channel) {
  case 'mobile':
    sourceId = constants.EPC_MOBILE;
    break;
  case 'tablet':
    sourceId = constants.EPC_TABLET;
    break;
  case 'desktop':
  default:
    sourceId = constants.EPC_DESKTOP;
  }
};

const getConfig = async () => {
  const headers = {
    ...defaultHeaders,
    ...(recaptchaTokenGenerator && { 'x-recaptcha-token': await recaptchaTokenGenerator(recaptchaAction) })
  };

  return { headers };
};

export const axiosPost = async ({
  url,
  data,
  callback,
  err
}) => {
  axios
    .post(url, data, await getConfig())
    .then(callback)
    .catch(err);
};

export const axiosPut = async ({
  url,
  data,
  callback,
  err
}) => {
  axios
    .put(url, data, await getConfig())
    .then(callback)
    .catch(err);
};

const buildUnsubscribeSmsRequest = (phoneNumber) => {
  return {
    sourceId,
    phoneNumber,
    globalOptOut: YES,
    subscriptions: [
      {
        type: PRO_TYPE,
        status: NO
      },
      {
        type: PROMOTIONS_TYPE,
        status: NO
      }
    ]
  };
};

const buildUnsubscribeEmailRequest = (emailAddress) => {
  return {
    sourceId,
    emailAddress,
    globalOptOut: YES,
    subscriptions: [
      {
        type: RETARGETING_TYPE,
        status: NO
      }
    ]
  };
};

const buildUnsubscribeSurveyEmailRequest = (emailAddress) => {
  return {
    sourceId,
    emailAddress,
    subscriptions: [
      {
        type: SURVEYS_TYPE,
        status: NO
      }
    ]
  };
};

const buildAddressRequest = (address) => {
  return {
    ...address,
    sourceId,
  };
};

export const unsubscribeFromDirectMail = (address, callback, err) => {
  const request = {
    ...buildAddressRequest(address),
    globalOptOut: YES,
  };

  axiosPut({
    url: epcUrls.UPDATE_DIRECT_MAIL,
    data: request,
    callback,
    err
  });
};

export const addressStandardization = (address, callback, err) => {
  const request = buildAddressRequest(address);

  axiosPost({
    url: epcUrls.STANDARDIZE_ADDRESS,
    data: request,
    callback,
    err
  });
};

export const retrieveEmailPreferences = ({ emailAddress, callback, err }) => {
  const request = {
    emailAddress
  };

  axiosPost({
    url: epcUrls.RETRIEVE_EMAIL,
    data: request,
    callback,
    err
  });
};

export const retrieveMarketingPhonePreferences = ({ phoneNumber, callback, err }) => {
  const request = {
    phoneNumber
  };
  axiosPost({
    url: epcUrls.MARKETING_PHONE_RETRIEVE,
    data: request,
    callback,
    err
  });
};

export const retrieveSmsPreferences = ({ phoneNumber, callback, err }) => {
  const request = {
    phoneNumber
  };
  axiosPost({
    url: epcUrls.RETRIEVE_SMS,
    data: request,
    callback,
    err
  });
};

// Process results
export const newSubscription = (type, status) => {
  return {
    type,
    status
  };
};

export const convertPrefsToSubs = (subs, table) => {
  return subs.map((sub) => newSubscription(table[sub].type, table[sub].value));
};

export const updateEmailPreferences = ({
  emailAddress,
  subscriptions,
  table = null,
  callback,
  err
}) => {
  const preferences = (table) ? convertPrefsToSubs(subscriptions, table) : subscriptions;
  const request = {
    sourceId,
    emailAddress,
    subscriptions: preferences
  };

  axiosPut({
    url: epcUrls.UPDATE_EMAIL,
    data: request,
    callback,
    err,
  });
};

export const updateTextPreferences = ({
  phoneNumber,
  subscriptions,
  table,
  callback,
  err
}) => {
  const preferences = convertPrefsToSubs(subscriptions, table);
  let request = {
    phoneNumber,
    sourceId,
    subscriptions: preferences
  };

  axiosPut({
    url: epcUrls.UPDATE_SMS,
    data: request,
    callback,
    err
  });
};

export const unsubscribeFromAllSmsSubscriptions = (phoneInput, callback, err) => {
  const request = buildUnsubscribeSmsRequest(phoneInput);

  axiosPut({
    url: epcUrls.UPDATE_SMS,
    data: request,
    callback,
    err
  });
};

export const unsubscribeFromAllEmailSubscriptions = (emailInput, callback, err) => {
  const request = buildUnsubscribeEmailRequest(emailInput);

  axiosPut({
    url: epcUrls.UPDATE_EMAIL,
    data: request,
    callback,
    err
  });
};

export const unsubscribeFromSurveyEmailSubscription = (emailInput, callback, err) => {
  const request = buildUnsubscribeSurveyEmailRequest(emailInput);

  axiosPut({
    url: epcUrls.UPDATE_EMAIL,
    data: request,
    callback,
    err
  });
};

export const resendText = (phoneNumber, table, callback, err) => {
  let preferences = Object.values(table).map((row) => {
    return row.isPending ? newSubscription(row.type, YES) : null;
  }).filter((row) => !!row);

  const request = {
    sourceId,
    phoneNumber,
    subscriptions: preferences
  };

  axiosPut({
    url: epcUrls.UPDATE_SMS,
    data: request,
    callback,
    err
  });
};

export const hasPendingStatus = (preferences) => {
  return preferences.some((pref) => pref.status === PENDING);
};

export const extractPermissionToContact = (response) => {
  return response?.data?.permissionToContact;
};

export const hasPermissionToContact = (response) => {
  return extractPermissionToContact(response) === YES;
};

export const hasData = (response) => {
  return !!response?.data;
};

export const getSubscriptions = (response) => {
  const res = response?.data;
  return res?.subscriptions || [];
};

export const getCombinedPhoneMarketingSubscriptions = (response) => {
  const res = response?.data;
  const smsPreferences = res?.smsSubscriptions || [];
  const telemarketingPreferences = res?.telemarketingSubscriptions === null ? [{ type: 'DEFAULT', status: YES }]
    : res?.telemarketingSubscriptions;
  return [...smsPreferences, ...telemarketingPreferences];
};

export const isFullyOptedOut = (response) => {
  const subs = getSubscriptions(response);
  if (!subs?.length) {
    return true;
  }
  return subs.some((sub) => sub?.status !== PENDING && sub?.status !== YES);
};

const flipValue = (value) => {
  return value === YES ? NO : YES;
};

export const uncheckCheckBoxes = (tableData, updates) => {
  let table = tableData;
  if (!updates) {
    Object.keys(table).forEach((update) => {
      table[update].value = NO;
    });
  } else {
    updates.forEach((update) => {
      table[update].value = flipValue(table[update].value);
    });
  }
  return table;
};

const emailValidationOptions = {
  tlds: { allow: tlds }
};

const emailSchema = Joi.object({ email: Joi.string().email(emailValidationOptions) });
export const emailValidator = (emailAddress) => {
  if (!emailAddress || emailAddress.length === 0) {
    return false;
  }
  return emailSchema.validate({ email: emailAddress }).error == null;
};

const convertStatusToValue = (status) => {
  return (status === YES) ? YES : NO;
};

export const convertSubsToPrefs = (tableData, subs) => {
  let table = tableData;

  subs.forEach((sub) => {
    const title = typeToTitleMap[sub.type];
    if (table[title]) {
      table[title].value = convertStatusToValue(sub.status);
      table[title].isPending = sub.status === PENDING;
    }
  });

  return table;
};

const exportFunctions = {
  convertSubsToPrefs,
  hasPendingStatus,
  hasData,
  getSubscriptions,
  emailValidator,
  uncheckCheckBoxes,
  unsubscribeFromDirectMail,
  unsubscribeFromAllEmailSubscriptions,
  resendText,
  isFullyOptedOut,
  hasPermissionToContact,
  retrieveMarketingPhonePreferences,
  getCombinedPhoneMarketingSubscriptions,
};

export default exportFunctions;
